import { useState, useEffect, useCallback } from 'react';
import { AppUser } from '@/types';
import { signInWithGooglePopup, loginWithApi, registerWithApi } from '../services/clientAuthService';
import { requestPasswordRecovery } from '../services/authRecoveryService';
import { updateUserProfileApi } from '../services/userProfileService';
import { getStoredUser, saveStoredUser } from './useAuthSession';

export const useAuth = () => {
  const [appUser, setAppUser] = useState<AppUser | null>(() => getStoredUser());
  const [loginError, setLoginError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [appUserLoading, setAppUserLoading] = useState<boolean>(false);
  const [passwordResetMessage, setPasswordResetMessage] = useState<string | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      const stored = getStoredUser();
      if (stored) {
        try {
          const res = await fetch(`/api/auth/me?userId=${stored.id || stored.uid}`);
          const data = await res.json();
          if (data.success && data.user) {
            setAppUser(data.user);
            saveStoredUser(data.user);
          }
        } catch {}
      }
      setAuthLoading(false);
    };
    initAuth();
  }, []);

  const signInWithEmail = useCallback(async (email: string, pass: string): Promise<boolean> => {
    setLoginError(null);
    setAppUserLoading(true);
    try {
      const user = await loginWithApi(email, pass);
      setAppUser(user);
      saveStoredUser(user);
      return true;
    } catch (err: any) {
      setLoginError(err?.message || 'Error al iniciar sesión');
      return false;
    } finally {
      setAppUserLoading(false);
    }
  }, []);

  const signUpWithEmail = useCallback(async (email: string, pass: string, name?: string): Promise<boolean> => {
    setLoginError(null);
    setAppUserLoading(true);
    try {
      const user = await registerWithApi(email, pass, name);
      setAppUser(user);
      saveStoredUser(user);
      return true;
    } catch (err: any) {
      setLoginError(err?.message || 'Error al crear cuenta');
      return false;
    } finally {
      setAppUserLoading(false);
    }
  }, []);

  const signInWithGoogle = useCallback(async (): Promise<boolean> => {
    setLoginError(null);
    setAppUserLoading(true);
    try {
      const googleData = await signInWithGooglePopup();
      const res = await fetch('/api/auth/google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: googleData.email, displayName: googleData.displayName, idToken: googleData.idToken }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error con Google Sign-In');
      setAppUser(data.user);
      saveStoredUser(data.user);
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Error al acceder con Google';
      if (!msg.includes('popup-closed-by-user')) setLoginError(msg);
      return false;
    } finally {
      setAppUserLoading(false);
    }
  }, []);

  const handlePasswordReset = useCallback(async (email: string): Promise<boolean> => {
    setPasswordResetMessage(null);
    setLoginError(null);
    const result = await requestPasswordRecovery(email);
    if (!result.success) {
      setLoginError(result.error || 'Error al restablecer contraseña');
      return false;
    }
    setPasswordResetMessage(result.message || `Enlace enviado a ${email}.`);
    return true;
  }, []);

  const logout = useCallback(async () => {
    setAppUser(null);
    saveStoredUser(null);
  }, []);

  const updateAppUserProfile = useCallback(async (
    arg1: string | Partial<AppUser>,
    arg2?: Partial<AppUser>
  ): Promise<boolean> => {
    const targetUid = typeof arg1 === 'string' ? arg1 : (appUser?.uid || appUser?.id);
    const fieldsToUpdate = typeof arg1 === 'object' ? arg1 : (arg2 || {});
    if (!targetUid) return false;

    const finalData = { ...fieldsToUpdate };
    if (finalData.commercializationCities && finalData.onboardingCompleted === undefined) {
      finalData.onboardingCompleted = true;
    }

    const merged = { ...(appUser || {}), ...finalData, id: targetUid, uid: targetUid } as AppUser;
    setAppUser(merged);
    saveStoredUser(merged);

    const res = await updateUserProfileApi(targetUid, finalData);
    if (res.success && res.user) {
      const serverMerged = { ...merged, ...res.user };
      setAppUser(serverMerged);
      saveStoredUser(serverMerged);
    }
    return true;
  }, [appUser]);

  return {
    firebaseUser: appUser ? { uid: appUser.id || appUser.uid, email: appUser.email } : null,
    appUser, loginError, authLoading, appUserLoading, passwordResetMessage,
    isEnvironmentReady: true, lastSignInMethod: null,
    loginWithEmailPassword: signInWithEmail, handleEmailPasswordSignIn: signInWithEmail,
    registerWithEmailPassword: signUpWithEmail, handleEmailPasswordSignUp: signUpWithEmail,
    loginWithGoogle: signInWithGoogle, handleGoogleSignIn: signInWithGoogle,
    sendPasswordReset: handlePasswordReset, handlePasswordReset,
    logout, handleLogout: logout,
    updateAppUserProfile, updateUserZohoId: updateAppUserProfile,
    handleCompleteOnboarding: updateAppUserProfile, refreshUserData: async () => {},
  };
};
